import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { SellerSettings } from '../enums/seller-settings/seller-settings';
import { SellerSettingsOptions } from '../enums/seller-settings/seller-settings-options';
import { labelFileTypeValues } from '../pages/settings/connect/constants';
import {
  labelDpis,
  batchSummaryOptions,
  labelOrderBys,
  defaultLabelFormatValue,
} from '../pages/settings/printing/document-option-modal/label/constants';
import { orderReportSortings } from '../pages/settings/printing/document-option-modal/order-summary/constants';
import { defaultPackingSlipFormat, packingSlipSortings } from '../pages/settings/printing/document-option-modal/packing-slip/constants';
import {
  pickListGroupings,
  pickListSortings,
  pickListWarehouseLocations,
} from '../pages/settings/printing/document-option-modal/pick-list/constants';
import { EnabledOnboardingVariantTypes } from '../pages/setup/constants';
import { PackingEnforcementMode } from '../packing/enforcement/constants';
import { SerialEnforcementLevel } from '../serial-numbers/enforcement/constants';
import { getSellerSettingOptions } from '../services/settings/seller-settings-service';
import Global from '../store/global-selectors';
import { useEnableStoreCurrencyForCustomsForm } from '../feature-flags/hooks/use-enable-store-currency-for-customs-form';

const DEFAULT_MOBILE_TOTE_QUANTITY = 25;

/**
 * @param {{key: string}} sellerSetting
 * @returns {string}
 */
export const useSellerSetting = (sellerSetting) => {
  const sellerSettingName = sellerSetting.key;
  return useSelector((state) => Global.selectSellerSellerSetting(state, { settingName: sellerSettingName }));
};

const getValueOrDefault = (settingValue, setting, defaultValue) => {
  const possibleValues = getSellerSettingOptions(setting);

  if (!!settingValue && possibleValues.some(({ value }) => settingValue === value)) {
    return settingValue;
  }

  return defaultValue;
};

// Same as getValueOrDefault, but treats a stored empty string as a valid value instead of falling back to defaultValue.
const getValueOrDefaultAllowEmpty = (settingValue, setting, defaultValue) => {
  const possibleValues = getSellerSettingOptions(setting);

  if (settingValue != null && possibleValues.some(({ value }) => settingValue === value)) {
    return settingValue;
  }

  return defaultValue;
};

// Case-insensitive variant used only for settings whose backend parsers apply
// Enum.TryParse(ignoreCase: true) — returns the canonical casing from the options
// map so downstream === comparisons stay correct even if the stored value has
// unexpected casing (e.g. 'warn' → 'Warn').
const getCanonicalValueOrDefault = (settingValue, setting, defaultValue) => {
  if (!settingValue) return defaultValue;

  const possibleValues = getSellerSettingOptions(setting);
  const match = possibleValues.find(({ value }) => value.toLowerCase() === settingValue.toLowerCase());

  return match?.value ?? defaultValue;
};

const getParsedValueOrDefault = (settingValue, setting, defaultValue) => {
  const possibleValues = getSellerSettingOptions(setting);
  try {
    const parsedValue = JSON.parse(settingValue);
    if (parsedValue !== undefined && possibleValues.some((possible) => possible.value === parsedValue)) {
      return parsedValue;
    }
  } catch {
    // do nothing
  }
  return defaultValue;
};

const getParsedObjectValueOrDefault = (settingValue, setting, defaultValue) => {
  const possibleValues = getSellerSettingOptions(setting);
  try {
    const parsedValue = JSON.parse(settingValue);
    if (parsedValue !== undefined && possibleValues.some((possible) => JSON.stringify(possible) === settingValue)) {
      return parsedValue;
    }
  } catch {
    // do nothing
  }
  return defaultValue;
};

const getStringValueOrDefault = (settingValue, defaultValue) => {
  return settingValue || defaultValue;
};

export const getParsedFloatValueOrDefault = (settingValue, defaultValue) => {
  const parsedFloat = Number.parseFloat(settingValue);
  if (Number.isNaN(parsedFloat)) {
    return defaultValue;
  }
  return parsedFloat;
};

export const useDimensionUnitOfMeasure = () =>
  getValueOrDefault(
    useSellerSetting(SellerSettings.dimensionUnitOfMeasure),
    SellerSettings.dimensionUnitOfMeasure,
    SellerSettingsOptions.inch.value,
  );

export const useWeightUnitOfMeasure = () =>
  getValueOrDefault(
    useSellerSetting(SellerSettings.weightUnitOfMeasure),
    SellerSettings.weightUnitOfMeasure,
    SellerSettingsOptions.ounce.value,
  );

export const useAdvancedProductDisplay = () => {
  const value = useSellerSetting(SellerSettings.useAdvancedProductDisplay);
  return getParsedValueOrDefault(value, SellerSettings.useAdvancedProductDisplay, SellerSettingsOptions.standard.value);
};

export const useFCMITracking = () => {
  const value = useSellerSetting(SellerSettings.fCMITracking);
  return getParsedValueOrDefault(value, SellerSettings.fCMITracking, true);
};

export const useBillDutiesAndTaxesToPayor = () => {
  const value = useSellerSetting(SellerSettings.billDutiesAndTaxesToPayor);
  return getParsedValueOrDefault(value, SellerSettings.billDutiesAndTaxesToPayor, false);
};

export const useCustomsStoreCurrency = () => {
  const isFeatureFlagEnabled = useEnableStoreCurrencyForCustomsForm();
  const value = useSellerSetting(SellerSettings.customsStoreCurrency);

  if (!isFeatureFlagEnabled) {
    return false;
  }

  return getParsedValueOrDefault(value, SellerSettings.customsStoreCurrency, false);
};

export const useCustomsSigner = () => useSellerSetting(SellerSettings.customsSigner) || '';

export const useCustomsContentType = () =>
  getValueOrDefault(
    useSellerSetting(SellerSettings.customsContentType),
    SellerSettings.customsContentType,
    SellerSettingsOptions.merchandise.value,
  );

export const useCustomsNonDelivery = () =>
  getValueOrDefault(
    useSellerSetting(SellerSettings.customsNonDelivery),
    SellerSettings.customsNonDelivery,
    SellerSettingsOptions.return.value,
  );

export const useCreateCustomsItems = () =>
  getValueOrDefault(
    useSellerSetting(SellerSettings.createCustomsItems),
    SellerSettings.createCustomsItems,
    SellerSettingsOptions.orderItems.value,
  );

export const useSellerFTUWizardJson = () => {
  const ftuSellerSetting = useSellerSetting(SellerSettings.sellerFTUWizardJson);
  return useMemo(() => {
    const stringValue = getStringValueOrDefault(ftuSellerSetting, '{}');
    return JSON.parse(stringValue);
  }, [ftuSellerSetting]);
};

export const useCustomsItemDescription = () => getStringValueOrDefault(useSellerSetting(SellerSettings.customsItemDescription), '');

export const useCustomsItemQty = () => getParsedFloatValueOrDefault(useSellerSetting(SellerSettings.customsItemQty), 1);

export const useCustomsItemValue = () => getParsedFloatValueOrDefault(useSellerSetting(SellerSettings.customsItemValue), 1);

export const useCustomsItemCountry = () =>
  getValueOrDefaultAllowEmpty(
    useSellerSetting(SellerSettings.customsItemCountry),
    SellerSettings.customsItemCountry,
    useSelector(Global.selectSellerHomeCountry),
  );

export const useCustomsItemHarmonization = () => getStringValueOrDefault(useSellerSetting(SellerSettings.customsItemHarmonization), '');

export const useExemptionOrITN = () =>
  getValueOrDefault(useSellerSetting(SellerSettings.exemptionOrITN), SellerSettings.exemptionOrITN, SellerSettingsOptions.empty.value);

export const useITNSetting = () =>
  getValueOrDefault(useSellerSetting(SellerSettings.iTNSetting), SellerSettings.iTNSetting, SellerSettingsOptions.empty.value);

export const useDefaultExemptionCode = () =>
  getValueOrDefault(
    useSellerSetting(SellerSettings.defaultExemptionCode),
    SellerSettings.defaultExemptionCode,
    SellerSettingsOptions.empty.value, // user can't pick empty, but is default to display
  );

export const useInventoryShipNoStock = () =>
  getValueOrDefault(useSellerSetting(SellerSettings.InventoryShipNoStock), SellerSettings.InventoryShipNoStock, '');

export const useInventoryShipUnknown = () =>
  getValueOrDefault(useSellerSetting(SellerSettings.InventoryShipUnknown), SellerSettings.InventoryShipUnknown, '');

export const useInventoryReOrderThreshold = () =>
  getParsedFloatValueOrDefault(useSellerSetting(SellerSettings.InventoryReOrderThreshold), 1);

export const useInventoryShipNoWarehouse = () =>
  getParsedValueOrDefault(useSellerSetting(SellerSettings.InventoryShipNoWarehouse), SellerSettings.InventoryShipNoWarehouse, false);

export const useInventoryTrackingEnabled = () =>
  getParsedValueOrDefault(useSellerSetting(SellerSettings.InventoryTrackingEnabled), SellerSettings.InventoryTrackingEnabled, false);

export const useOrderReportSorting = () =>
  getValueOrDefault(useSellerSetting(SellerSettings.orderReportSorting), SellerSettings.orderReportSorting, orderReportSortings[0].value);

export const useHideOrderReportImages = () =>
  getParsedValueOrDefault(useSellerSetting(SellerSettings.hideOrderReportImages), SellerSettings.hideOrderReportImages, false);

export const useSimpleOrderSummary = () =>
  getParsedValueOrDefault(useSellerSetting(SellerSettings.simpleOrderSummary), SellerSettings.simpleOrderSummary, false);

export const useOrderSummaryMasterSku = () =>
  getParsedValueOrDefault(useSellerSetting(SellerSettings.orderSummaryMasterSku), SellerSettings.orderSummaryMasterSku);

export const useOrderSummaryScanToViewBarcode = () =>
  getParsedValueOrDefault(
    useSellerSetting(SellerSettings.orderSummaryScanToViewBarcode),
    SellerSettings.orderSummaryScanToViewBarcode,
    false,
  );

export const useReturnService = () => useSellerSetting(SellerSettings.returnService) || '';
export const useReturnServiceCollectionGuid = () => useSellerSetting(SellerSettings.returnServiceCollectionGuid) ?? null;
export const useReturnPackage = () => useSellerSetting(SellerSettings.returnPackage) || '';
export const useReturnTracking = () =>
  getParsedValueOrDefault(useSellerSetting(SellerSettings.returnTracking), SellerSettings.returnTracking);

export const useIsAutoSplitSellerSettingEnabled = () =>
  getParsedValueOrDefault(useSellerSetting(SellerSettings.AutoSplitEnabled), SellerSettings.AutoSplitEnabled, false);

export const useIsSmartFillSellerSettingEnabled = () =>
  getParsedValueOrDefault(useSellerSetting(SellerSettings.isSmartFillActive), SellerSettings.isSmartFillActive, false);

export const useShouldSmartFillOverwriteMarketplaceWeight = () =>
  getParsedValueOrDefault(
    useSellerSetting(SellerSettings.shouldSmartFillOverwriteMarketplaceWeight),
    SellerSettings.shouldSmartFillOverwriteMarketplaceWeight,
    false,
  );

export const useTotalOrderWithSmartFillApplied = () =>
  getParsedFloatValueOrDefault(useSellerSetting(SellerSettings.totalOrderWithSmartFillApplied), 0);

export const useIsSmartFillWeightEnabled = () =>
  getParsedValueOrDefault(useSellerSetting(SellerSettings.isSmartFillWeightEnabled), SellerSettings.isSmartFillWeightEnabled, false);

export const useIsSmartFillSizeEnabled = () =>
  getParsedValueOrDefault(useSellerSetting(SellerSettings.isSmartFillSizeEnabled), SellerSettings.isSmartFillSizeEnabled, false);

export const useIsSmartFillPackageEnabled = () =>
  getParsedValueOrDefault(useSellerSetting(SellerSettings.isSmartFillPackageEnabled), SellerSettings.isSmartFillPackageEnabled, false);

export const useIsSmartFillHashKeyManagementEnabled = () =>
  getParsedValueOrDefault(
    useSellerSetting(SellerSettings.isSmartFillHashKeyManagementEnabled),
    SellerSettings.isSmartFillHashKeyManagementEnabled,
    false,
  );

export const useIsRefundAssistEnabled = () =>
  getParsedValueOrDefault(useSellerSetting(SellerSettings.isRefundAssistEnabled), SellerSettings.isRefundAssistEnabled, false);

export const usePickListGrouping = () =>
  getValueOrDefault(useSellerSetting(SellerSettings.pickListGrouping), SellerSettings.pickListGrouping, pickListGroupings[0].value);

export const usePickListSorting = () =>
  getValueOrDefault(useSellerSetting(SellerSettings.pickListSorting), SellerSettings.pickListSorting, pickListSortings[0].value);

export const usePickListImages = () =>
  getParsedValueOrDefault(useSellerSetting(SellerSettings.pickListImages), SellerSettings.pickListImages, false);

export const usePickListMasterSku = () =>
  getParsedValueOrDefault(useSellerSetting(SellerSettings.pickListMasterSku), SellerSettings.pickListMasterSku, false);

export const usePickListZeroQuantity = () =>
  getParsedValueOrDefault(useSellerSetting(SellerSettings.pickListZeroQuantity), SellerSettings.pickListZeroQuantity, false);

export const usePickListInventoryCounts = () =>
  getParsedValueOrDefault(useSellerSetting(SellerSettings.pickListInventoryCounts), SellerSettings.pickListInventoryCounts, false);

export const usePickListWarehouseLocation = () =>
  getValueOrDefault(
    useSellerSetting(SellerSettings.pickListWarehouseLocation),
    SellerSettings.pickListWarehouseLocation,
    pickListWarehouseLocations[0].value,
  );

export const usePickListOrderNumbers = () =>
  getParsedValueOrDefault(useSellerSetting(SellerSettings.pickListOrderNumbers), SellerSettings.pickListOrderNumbers, false);

export const usePickListIncludeLotId = () =>
  getParsedValueOrDefault(useSellerSetting(SellerSettings.pickListIncludeLotId), SellerSettings.pickListIncludeLotId, false);

export const usePickListHideItemOptions = () =>
  getParsedValueOrDefault(useSellerSetting(SellerSettings.pickListHideItemOptions), SellerSettings.pickListHideItemOptions, false);

export const usePickListDefaultTemplateKey = () => useSellerSetting(SellerSettings.pickListDefaultTemplateKey);

export const usePackingSlipFormat = () =>
  getValueOrDefault(useSellerSetting(SellerSettings.packingSlipFormat), SellerSettings.packingSlipFormat, defaultPackingSlipFormat.value);

export const usePackingSlipSorting = () =>
  getValueOrDefault(useSellerSetting(SellerSettings.packingSlipSorting), SellerSettings.packingSlipSorting, packingSlipSortings[0].value);

export const usePackingSlipLogos = () =>
  getParsedValueOrDefault(useSellerSetting(SellerSettings.packingSlipLogos), SellerSettings.packingSlipLogos, false);

export const usePackingSlipPrices = () =>
  getParsedValueOrDefault(useSellerSetting(SellerSettings.packingSlipPrices), SellerSettings.packingSlipPrices, false);

export const usePackingSlipSkus = () =>
  getParsedValueOrDefault(useSellerSetting(SellerSettings.packingSlipSkus), SellerSettings.packingSlipSkus, false);

export const usePackingSlipBarcode = () =>
  getParsedValueOrDefault(useSellerSetting(SellerSettings.packingSlipBarcode), SellerSettings.packingSlipBarcode, false);

export const usePackingSlipInventoryLocation = () =>
  getParsedValueOrDefault(
    useSellerSetting(SellerSettings.packingSlipInventoryLocation),
    SellerSettings.packingSlipInventoryLocation,
    false,
  );

export const usePackingSlipZeroQuantity = () =>
  getParsedValueOrDefault(useSellerSetting(SellerSettings.packingSlipZeroQuantity), SellerSettings.packingSlipZeroQuantity, false);

export const usePackingSlipShowDiscounts = () =>
  getParsedValueOrDefault(useSellerSetting(SellerSettings.packingSlipShowDiscounts), SellerSettings.packingSlipShowDiscounts, false);

export const usePackingSlipReturnQrCode = () =>
  getParsedValueOrDefault(useSellerSetting(SellerSettings.packingSlipReturnQrCode), SellerSettings.packingSlipReturnQrCode, false);

export const useSerialNumberEnforcementSetting = () =>
  getCanonicalValueOrDefault(
    useSellerSetting(SellerSettings.serialNumberEnforcement),
    SellerSettings.serialNumberEnforcement,
    SerialEnforcementLevel.Block,
  );

export const usePackingRecommendationEnforcementModeSetting = () =>
  getCanonicalValueOrDefault(
    useSellerSetting(SellerSettings.packingRecommendationEnforcementMode),
    SellerSettings.packingRecommendationEnforcementMode,
    PackingEnforcementMode.Warning,
  );

export const useShipmentManifestItems = () =>
  getParsedValueOrDefault(useSellerSetting(SellerSettings.shipmentManifestItems), SellerSettings.shipmentManifestItems, false);

export const useLabelOutput = () =>
  getValueOrDefault(useSellerSetting(SellerSettings.labelOutput), SellerSettings.labelOutput, defaultLabelFormatValue);

export const useLabelDpi = () => getValueOrDefault(useSellerSetting(SellerSettings.labelDpi), SellerSettings.labelDpi, labelDpis[0].value);

export const useBatchSummaryPage = () =>
  getValueOrDefault(useSellerSetting(SellerSettings.batchSummaryPage), SellerSettings.batchSummaryPage, batchSummaryOptions[0].value);

export const useLabelPrintOrder = () =>
  getValueOrDefault(useSellerSetting(SellerSettings.labelPrintOrder), SellerSettings.labelPrintOrder, labelOrderBys[0].value);

export const useLabelLogos = () =>
  getParsedValueOrDefault(useSellerSetting(SellerSettings.enableLabelLogos), SellerSettings.enableLabelLogos, false);

export const useLabelFileType = () =>
  getValueOrDefault(useSellerSetting(SellerSettings.labelFileType), SellerSettings.labelFileType, labelFileTypeValues[0]);

export const useRubberStamp1 = () => useSellerSetting(SellerSettings.rubberStamp1);

export const useRubberStamp2 = () => useSellerSetting(SellerSettings.rubberStamp2);

export const useRubberStamp3 = () => useSellerSetting(SellerSettings.rubberStamp3);

export const useExtendShipDate = () =>
  getParsedValueOrDefault(useSellerSetting(SellerSettings.extendShipDate), SellerSettings.extendShipDate, false);

export const useOnboardingLabelStepComplete = () =>
  getParsedValueOrDefault(useSellerSetting(SellerSettings.onboardLabelStepComplete), SellerSettings.onboardLabelStepComplete, false);

export const useExtendShipDateAfter = () => useSellerSetting(SellerSettings.extendShipDateAfter);

export const useSetupComplete = () =>
  getParsedValueOrDefault(useSellerSetting(SellerSettings.setupComplete), SellerSettings.setupComplete, false);

export const useIsQuickshipAllowed = () => {
  const value = getParsedValueOrDefault(useSellerSetting(SellerSettings.isQuickshipAllowed), SellerSettings.isQuickshipAllowed, false);
  return value;
};

export const useCustomLabelLayouts = () =>
  getParsedObjectValueOrDefault(useSellerSetting(SellerSettings.customLabelLayouts), SellerSettings.customLabelLayouts, []);

export const useSellerOnboardingVariant = () =>
  getValueOrDefault(
    useSellerSetting(SellerSettings.sellerOnboardingVariant),
    SellerSettings.sellerOnboardingVariant,
    EnabledOnboardingVariantTypes.HIDE_PROGRESSIVE_DISCLOSURE,
  );

export const useSellerOnboardingJson = () => {
  const stringValue = getStringValueOrDefault(useSellerSetting(SellerSettings.sellerOnboardingJson), '{}');
  return JSON.parse(stringValue);
};

export const useSmartRoutingSellerSetting = () => {
  return getParsedValueOrDefault(useSellerSetting(SellerSettings.isSmartRoutingEnabled), SellerSettings.isSmartRoutingEnabled, false);
};

export const useSmartRoutingSetupCompleteSellerSetting = () => {
  return getParsedValueOrDefault(
    useSellerSetting(SellerSettings.isSmartOrderRoutingSetupComplete),
    SellerSettings.isSmartOrderRoutingSetupComplete,
    false,
  );
};

export const useCustomRateShopperSetupCompleteSellerSetting = () => {
  return getParsedValueOrDefault(
    useSellerSetting(SellerSettings.isCustomRateShopperSetupComplete),
    SellerSettings.isCustomRateShopperSetupComplete,
    false,
  );
};

export const useAutomationRulesSetupCompleteSellerSetting = () => {
  return getParsedValueOrDefault(
    useSellerSetting(SellerSettings.isAutomationRulesSetupComplete),
    SellerSettings.isAutomationRulesSetupComplete,
    false,
  );
};

export const useProductPresetsSetupCompleteSellerSetting = () => {
  return getParsedValueOrDefault(
    useSellerSetting(SellerSettings.isProductPresetsSetupComplete),
    SellerSettings.isProductPresetsSetupComplete,
    false,
  );
};

export const useServiceMappingsSetupCompleteSellerSetting = () => {
  return getParsedValueOrDefault(
    useSellerSetting(SellerSettings.isServiceMappingsSetupComplete),
    SellerSettings.isServiceMappingsSetupComplete,
    false,
  );
};

export const useCubiscanSellerSetting = () =>
  getParsedValueOrDefault(useSellerSetting(SellerSettings.enableCubiscan), SellerSettings.enableCubiscan, false);

export const useODBCSellerSetting = () =>
  getParsedValueOrDefault(useSellerSetting(SellerSettings.isODBCEnabled), SellerSettings.isODBCEnabled, false);

export const useSelectedFulfillmentStrategySellerSetting = () => {
  const stringValue = getStringValueOrDefault(useSellerSetting(SellerSettings.selectedFulfillmentStrategy), '');
  return stringValue;
};

export const useFulfillmentProvidersForAutoRoutingSellerSetting = () => {
  return getParsedValueOrDefault(
    useSellerSetting(SellerSettings.shouldUseFulfillmentProviders),
    SellerSettings.shouldUseFulfillmentProviders,
    false,
  );
};

export const useIsAccountEmailVerifiedSellerSetting = () =>
  getParsedValueOrDefault(useSellerSetting(SellerSettings.isAccountEmailVerified), SellerSettings.isAccountEmailVerified, false);

export const useBTEmailExperienceExisting = () =>
  getParsedValueOrDefault(
    useSellerSetting(SellerSettings.brandedTrackingEmailExperienceExisting),
    SellerSettings.brandedTrackingEmailExperienceExisting,
    false,
  );

export const useBTEmailExperienceNew = () =>
  getParsedValueOrDefault(
    useSellerSetting(SellerSettings.brandedTrackingEmailExperienceNew),
    SellerSettings.brandedTrackingEmailExperienceNew,
    false,
  );

export const useEnhancedReturnsAccountStatus = () =>
  getStringValueOrDefault(useSellerSetting(SellerSettings.enhancedReturnsAccountStatus), '');

export const useMobileDefaultToteQuantity = () =>
  getParsedFloatValueOrDefault(useSellerSetting(SellerSettings.mobileDefaultToteQuantity), DEFAULT_MOBILE_TOTE_QUANTITY);

export const useMobileRequireLocationVerification = () =>
  getParsedValueOrDefault(
    useSellerSetting(SellerSettings.mobileRequireLocationVerification),
    SellerSettings.mobileRequireLocationVerification,
    false,
  );

export const useMobileShowLocationStep = () =>
  getParsedValueOrDefault(useSellerSetting(SellerSettings.mobileShowLocationStep), SellerSettings.mobileShowLocationStep, true);

export const useMobileSortItemsBy = () => getStringValueOrDefault(useSellerSetting(SellerSettings.mobileSortItemsBy), 'Location');

export const useMobileManualItemConfirmation = () =>
  getStringValueOrDefault(useSellerSetting(SellerSettings.mobileManualItemConfirmation), 'AllAtOnce');

/**
 * @returns {boolean}
 */
export const useUnlockFulfilledByShipstationASN = () =>
  getParsedValueOrDefault(
    useSellerSetting(SellerSettings.unlockFulfilledByShipstationASN),
    SellerSettings.unlockFulfilledByShipstationASN,
    false,
  );

/**
 * If enabled, all returns will be handled by Shipstation Returns (ReturnRabbit).
 * @returns {boolean}
 */
export const useIsShipstationReturnsEnabled = () => useSellerSetting(SellerSettings.enableShipstationReturns)?.toLowerCase() === 'true';

/**
 * Whether the seller opted in to the Pack Station (new scan page). Absent setting counts as false,
 * so existing sellers stay on the classic Scan page until they opt in (SPD-28197).
 * @returns {boolean}
 */
export const useIsNewScanPageEnabled = () => useSellerSetting(SellerSettings.useNewScanPage)?.toLowerCase() === 'true';

/**
 * @returns {boolean}
 */
export const useIsBuyNow = () => {
  const setting = useSellerSetting(SellerSettings.isBuyNow);
  return setting?.optedInToBuyNow ?? false;
};

export const usePhoneLastUpdatedUtc = () => getStringValueOrDefault(useSellerSetting(SellerSettings.phoneLastUpdatedUtc), '');

export const useSellerRegistrationApp = () => getStringValueOrDefault(useSellerSetting(SellerSettings.sellerRegistrationApp), '');
